import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { inspectionTypeService } from '@/services/inspectionType.service';
import { checklistService } from '@/services/checklists';
import { useAlertContext } from '@/contexts/alert.context';
import { useChecklistContext } from './contexts/ChecklistContext';

export function useChecklistNew() {
  const navigation = useNavigate();
  const { id: checklistId } = useParams<{ id?: string }>();
  const isEditMode = !!checklistId;
  const { notify } = useAlertContext();
  const { formatForBackend, loadInitialData } = useChecklistContext();

  const [name, setName] = useState('');
  const [selectedInspectionType, setSelectedInspectionType] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState('montagem');
  const [inspectionTypes, setInspectionTypes] = useState<{ value: string; label: string }[]>([]);
  const [loadingInspectionTypes, setLoadingInspectionTypes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadInspectionTypes = async () => {
      setLoadingInspectionTypes(true);
      try {
        const types = await inspectionTypeService.getAllInspectionTypes();
        const options = types
          .filter((type) => type.isActive)
          .map((type) => ({
            value: type.id,
            label: type.name,
          }));
        setInspectionTypes(options);
      } catch {
        // Ignore errors loading inspection types
      } finally {
        setLoadingInspectionTypes(false);
      }
    };

    loadInspectionTypes();
  }, []);

  useEffect(() => {
    if (!isEditMode || !checklistId) return;

    const loadChecklist = async () => {
      setLoading(true);
      try {
        const checklist = await checklistService.getChecklistById(checklistId);

        setName(checklist.name);
        setSelectedInspectionType(checklist.inspectionTypeId);

        const transformedGroups = checklist.groups.map((group: any) => ({
          id: group.id,
          name: group.name,
          order: group.order,
          sections: group.sections.map((section: any) => ({
            id: section.id,
            name: section.name,
            order: section.order,
            questions: section.questions.map((question: any) => ({
              id: question.id,
              title: question.title,
              answerType: question.answerType,
              order: question.order,
              isRequired: question.isRequired,
              showInSimplifiedReport: question.showInSimplifiedReport,
              hasHint: question.hasHint,
              hintText: question.hintText,
              hasDisplayRule: question.hasDisplayRule,
              displayRule: question.displayRule,
              options: question.options,
              maxCharacters: question.maxCharacters,
              decimalPlaces: question.decimalPlaces,
              maxPhotos: question.maxPhotos,
              allowGallery: question.allowGallerySelection,
              maxFiles: question.maxFiles,
              allowedFileTypes: question.allowedFileTypes,
              columns: Array.isArray(question.tableColumns)
                ? question.tableColumns
                    .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                    .map((col: any) => {
                      if (typeof col === 'string') return col;
                      if (col && typeof col === 'object' && col.name) return col.name;
                      return String(col);
                    })
                : [],
              tags: Array.isArray(question.tags)
                ? question.tags.map((tag: any) => {
                    if (typeof tag === 'string') return tag;
                    if (tag && typeof tag === 'object' && tag.name) return tag.name;
                    return String(tag);
                  })
                : [],
              template: question.template || undefined,
              description: question.description || undefined,
            })),
          })),
        }));

        loadInitialData(transformedGroups);
      } catch (err: any) {
        notify({
          message: err.message || 'Não foi possível carregar o checklist',
          type: 'error',
        });
        navigation('/checklists');
      } finally {
        setLoading(false);
      }
    };

    loadChecklist();
  }, [isEditMode, checklistId]);

  const handleBack = () => {
    navigation('/checklists');
  };

  const handleSave = async () => {
    if (!name || name.trim() === '') {
      notify({
        message: 'Por favor, preencha o nome do checklist',
        type: 'error',
      });
      return;
    }

    if (!selectedInspectionType) {
      notify({
        message: 'Por favor, selecione um tipo de inspeção',
        type: 'error',
      });
      return;
    }

    const workspaceData = formatForBackend();

    if (!workspaceData.groups || workspaceData.groups.length === 0) {
      notify({
        message: 'Por favor, adicione pelo menos um grupo ao checklist',
        type: 'error'
      });
      return;
    }

    const groupsWithoutSections = workspaceData.groups.filter(g => !g.sections || g.sections.length === 0);
    if (groupsWithoutSections.length > 0) {
      notify({
        message: 'Todos os grupos devem ter pelo menos uma seção',
        type: 'error'
      });
      return;
    }

    for (const group of workspaceData.groups) {
      for (const section of group.sections) {
        for (const question of section.questions) {
          if (question.hasHint && (!question.hintText || question.hintText.trim() === '')) {
            notify({
              message: `A pergunta "${question.title}" no grupo "${group.name}" - seção "${section.name}" tem dica ativada mas não possui texto de dica`,
              type: 'error'
            });
            return;
          }
        }
      }
    }

    for (const group of workspaceData.groups) {
      const sectionsWithoutQuestions = group.sections.filter(s => !s.questions || s.questions.length === 0);
      if (sectionsWithoutQuestions.length > 0) {
        notify({
          message: `Todas as seções do grupo "${group.name}" devem ter pelo menos uma pergunta`,
          type: 'error',
        });
        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        checklistName: name.trim(),
        inspectionTypeId: selectedInspectionType,
        ...workspaceData,
      };

      if (isEditMode && checklistId) {
        await checklistService.updateChecklist(checklistId, payload);
        notify({
          message: 'Checklist atualizado com sucesso!',
          type: 'success',
        });
      } else {
        await checklistService.createChecklist(payload);
        notify({
          message: 'Checklist criado com sucesso!',
          type: 'success',
        });
      }

      navigation('/checklists');
    } catch (err: any) {
      notify({
        message: err.message || 'Não foi possível salvar o checklist',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    name,
    setName,
    selectedInspectionType,
    setSelectedInspectionType,
    inspectionTypes,
    loadingInspectionTypes,
    activeTab,
    setActiveTab,
    handleBack,
    handleSave,
    saving,
    isEditMode,
    loading,
  };
}
